import React, { useState, useEffect } from 'react';
import { 
  TrendingDown, Sparkles, Mail, Lock, X, 
  LockKeyhole, BarChart3, Gem, LineChart, Cpu, Zap, Activity,
  ChevronRight, Terminal, ShieldAlert, Globe, List, Target,
  Coins, Loader2
} from 'lucide-react';

const App: React.FC = () => {
  const [newsInput, setNewsInput] = useState('U.S. CPI rose 0.3% in Dec, slightly above expectations. Markets re-evaluating Fed rate cuts.');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [hasUsedTrial, setHasUsedTrial] = useState(false);

  const loadingMessages = [
    'Analyzing context...',
    'Running NLP vectors...',
    'Calibrating institutional sentiment...',
    'Aggregating desk logs...'
  ];

  useEffect(() => {
    let interval: number;
    if (isProcessing) {
      interval = window.setInterval(() => {
        setLoadingStep((prev) => (prev + 1) % loadingMessages.length);
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isProcessing]);

  const handleProcessData = () => {
    if (hasUsedTrial) return;
    
    setIsProcessing(true);
    setShowResults(false);
    
    setTimeout(() => {
      setIsProcessing(false);
      setShowResults(true);
      setHasUsedTrial(true);
    }, 2500);
  };

  const ExpansionCard = ({ title, subtitle, icon }: { title: string, subtitle: string, icon: React.ReactNode }) => (
    <div className="relative overflow-hidden bg-white border border-slate-200 rounded-3xl p-8 text-center flex flex-col justify-center items-center h-72 transition-all duration-500 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-500/5 opacity-70">
      <div className="text-slate-900 mb-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
        {icon}
      </div>
      <div className="mb-6 px-3 py-1 bg-blue-50 rounded-full">
        <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">SOON</span>
      </div>
      <div className="flex flex-col items-center">
        <h4 className="text-lg font-black tracking-tighter text-slate-900 uppercase italic">{title}</h4>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">{subtitle}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen selection:bg-cyan-100 bg-[#f8fafc]">
      
      {/* NAVIGATION */}
      <nav className="fixed top-0 w-full z-50 glass-light h-20 flex items-center px-6 md:px-12 justify-center">
        <div className="max-w-6xl w-full flex justify-between items-center">
          <div className="flex items-center gap-1 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <span className="font-bold text-2xl tracking-tighter text-slate-900">SENTIX<span className="text-cyan-600">.</span></span>
          </div>
          <button 
            className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full text-[11px] font-bold uppercase tracking-widest transition-all shadow-lg shadow-slate-200 active:scale-95"
          >
            Join Beta
          </button>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 pt-40 pb-32">
        
        {/* HERO SECTION */}
        <section className="text-center space-y-8 mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-slate-200 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-widest mx-auto shadow-sm">
            <Zap size={12} className="text-cyan-600 fill-cyan-600" /> One-Time Intelligence Preview
          </div>
          <h1 className="text-6xl md:text-7xl font-black tracking-tighter leading-[1.1] text-slate-900 uppercase max-w-4xl mx-auto">
            Trade the News <br className="hidden md:block" /> with institutional edge.
          </h1>
        </section>

        {/* ANALYZER BLOCK */}
        <section className="bg-slate-900 rounded-[3rem] p-6 md:p-12 lg:p-16 shadow-bloomberg border border-slate-800 mb-32 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 blur-[100px] -z-0"></div>
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch relative z-10">
            
            {/* LEFT: INPUT AREA */}
            <div className="lg:col-span-5 flex flex-col space-y-6">
              <div className="flex items-center gap-3">
                <Cpu size={18} className="text-cyan-400" />
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-[0.3em]">Market News NLP Analyzer</h2>
              </div>
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-3xl p-8 flex-grow shadow-inner">
                <textarea 
                  value={newsInput}
                  onChange={(e) => setNewsInput(e.target.value)}
                  disabled={hasUsedTrial}
                  className="w-full bg-transparent text-xl font-medium text-slate-100 outline-none h-64 resize-none leading-relaxed placeholder:text-slate-600 disabled:opacity-50"
                  placeholder="Paste news body here..."
                />
              </div>
              <button 
                onClick={handleProcessData}
                disabled={isProcessing || hasUsedTrial}
                className="w-full py-6 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black text-[13px] uppercase tracking-[0.3em] shadow-2xl shadow-blue-900/40 transition-all flex items-center justify-center gap-3 active:scale-[0.98] disabled:bg-slate-800 disabled:shadow-none"
              >
                {isProcessing ? (
                  <div className="flex items-center gap-3">
                    <Loader2 size={18} className="animate-spin text-cyan-400" />
                    <span className="animate-pulse tracking-widest">{loadingMessages[loadingStep]}</span>
                  </div>
                ) : hasUsedTrial ? (
                  "TRIAL USED"
                ) : (
                  <>PROCESS DATA <ChevronRight size={18} /></>
                )}
              </button>
            </div>

            {/* RIGHT: RESULTS PANEL (WITH PAYWALL) */}
            <div className="lg:col-span-7">
              <div className={`bg-slate-950 border border-cyan-500/20 rounded-[2.5rem] h-full shadow-2xl transition-all duration-700 flex flex-col relative overflow-hidden ${!showResults && !isProcessing ? 'opacity-30' : ''}`}>
                
                {/* STATUS BAR */}
                <div className="bg-slate-900/80 px-8 py-4 border-b border-slate-800 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${showResults ? 'bg-emerald-400 shadow-[0_0_8px_#34d399]' : 'bg-slate-600 animate-pulse'}`}></div>
                    <span className={`text-[10px] font-black tracking-[0.2em] uppercase ${showResults ? 'text-emerald-400' : 'text-slate-500'}`}>
                      {showResults ? 'NLP ANALYSIS COMPLETED' : isProcessing ? 'PROCESSING AGGREGATE DATA' : 'ENGINE STANDBY'}
                    </span>
                  </div>
                  <Terminal size={14} className="text-slate-600" />
                </div>

                <div className="flex-grow flex flex-col relative">
                  {showResults ? (
                    <div className="p-10 font-mono space-y-10 relative h-full">
                      
                      {/* Sentiment Vector - ALWAYS VISIBLE */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Sentiment Vector</span>
                          <span className="text-2xl font-black text-rose-400">-42% <span className="text-[10px] uppercase ml-1 opacity-50">Bearish</span></span>
                        </div>
                        <div className="h-3 w-full bg-slate-900 rounded-full overflow-hidden border border-slate-800 relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-rose-500/40 via-slate-800 to-emerald-500/40"></div>
                          <div className="absolute top-0 bottom-0 right-1/2 bg-rose-500 shadow-[0_0_15px_#f43f5e] z-10 animate-in slide-in-from-right duration-1000" style={{ width: '21%' }}></div>
                          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20 z-20"></div>
                        </div>
                        <p className="text-[11px] text-slate-400 italic">
                          // Bearish divergence detected in institutional news-flow.
                        </p>
                      </div>

                      {/* BLURRED PAYWALL SECTION */}
                      <div className="space-y-10 filter blur-[8px] pointer-events-none select-none">
                        <div className="space-y-4">
                          <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Key Drivers Detected</div>
                          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 space-y-3">
                            <div className="flex items-start gap-3 text-[11px] text-slate-200">
                              <span className="text-cyan-400">•</span>
                              <span>Higher than expected CPI (+0.1% deviation)</span>
                            </div>
                            <div className="flex items-start gap-3 text-[11px] text-slate-200">
                              <span className="text-cyan-400">•</span>
                              <span>Hawkish FED outlook reinforcement</span>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Operational Bias</div>
                          <div className="bg-rose-500/10 border border-rose-500/20 py-4 px-6 rounded-2xl text-center">
                            <span className="text-lg font-black text-rose-400 uppercase tracking-tighter italic">
                              SHORT-TERM BEARISH / HIGH VOLATILITY
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* LOCK OVERLAY */}
                      <div className="absolute inset-0 top-[180px] z-30 flex flex-col items-center justify-center p-8 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-1000">
                        <div className="bg-slate-900/90 border border-slate-700 p-10 rounded-[2.5rem] w-full max-w-md text-center space-y-6 shadow-2xl">
                          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto shadow-xl shadow-blue-500/20">
                            <LockKeyhole size={28} className="text-white" />
                          </div>
                          <div className="space-y-2">
                            <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter">Full Analysis Locked</h3>
                            <p className="text-[11px] font-medium text-slate-400 leading-relaxed px-4">
                              Join 100+ traders in the Beta to unlock detailed institutional insights and access NASDAQ & Gold modules.
                            </p>
                          </div>
                          
                          {/* EMBEDDED FORM */}
                          <form action="https://formspree.io/f/mqaelonr" method="POST" className="space-y-3 pt-4">
                            <input 
                              type="email" 
                              name="email" 
                              required 
                              placeholder="Your trading email..." 
                              className="w-full px-6 py-4 bg-slate-800 border border-slate-700 rounded-xl text-white outline-none focus:border-cyan-500 text-sm font-bold transition-all"
                            />
                            <button 
                              type="submit" 
                              className="w-full py-4 bg-white text-black rounded-xl font-black text-[11px] uppercase tracking-widest hover:bg-cyan-400 transition-all active:scale-95"
                            >
                              Unlock Beta Access
                            </button>
                          </form>
                        </div>
                      </div>

                    </div>
                  ) : (
                    <div className="flex-grow flex flex-col items-center justify-center p-20 text-center space-y-6">
                      {isProcessing ? (
                        <div className="space-y-6">
                          <div className="relative">
                            <Activity size={48} className="text-cyan-400 animate-spin mx-auto" />
                            <div className="absolute inset-0 blur-2xl bg-cyan-500/20 animate-pulse"></div>
                          </div>
                          <div className="space-y-2">
                            <p className="text-sm font-black text-white uppercase tracking-[0.3em]">{loadingMessages[loadingStep]}</p>
                            <p className="text-[9px] text-slate-600 font-mono uppercase tracking-widest">Synthesizing sentiment vectors...</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="w-20 h-20 bg-slate-900 rounded-3xl flex items-center justify-center border border-slate-800 mb-2 group-hover:scale-110 transition-transform">
                             <Target size={36} className="text-slate-700" />
                          </div>
                          <p className="text-[11px] text-slate-500 font-black uppercase tracking-[0.4em] leading-relaxed max-w-[240px]">
                            Awaiting news input for one-time institutional calibration
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="mt-auto bg-slate-900/50 p-5 border-t border-slate-800 flex justify-between rounded-b-[2.5rem]">
                   <span className="text-[8px] font-black text-slate-600 tracking-widest uppercase italic">Engine: Sentix_v4_core</span>
                   <span className="text-[8px] font-black text-slate-600 tracking-widest uppercase italic">Institutional Feed Active</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* EXPANSION MODULES */}
        <section className="mb-32 space-y-12">
          <div className="text-center">
            <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-[0.5em] mb-4">Expansion</h2>
            <p className="text-3xl font-black text-slate-900 uppercase italic tracking-tight">Upcoming Markets</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <ExpansionCard title="NASDAQ 100" subtitle="Tech Index Analysis" icon={<LineChart size={32} strokeWidth={1.5} />} />
            <ExpansionCard title="GOLD (XAU/USD)" subtitle="Commodity Sentiment" icon={<Gem size={32} strokeWidth={1.5} />} />
            <ExpansionCard title="S&P 500" subtitle="Market Breadth" icon={<BarChart3 size={32} strokeWidth={1.5} />} />
            <ExpansionCard title="FOREX (MAJORS)" subtitle="Currency Flow" icon={<Globe size={32} strokeWidth={1.5} />} />
          </div>
        </section>

        {/* FOOTER */}
        <footer className="mt-40 pt-12 border-t border-slate-200 text-center">
          <div className="flex items-center justify-center gap-8 mb-8">
            <a href="#" className="text-[10px] font-bold text-slate-500 uppercase tracking-widest hover:text-slate-900 transition-colors">X / Twitter</a>
            <a href="#" className="text-[10px] font-bold text-slate-500 uppercase tracking-widest hover:text-slate-900 transition-colors">Documentation</a>
            <a href="#" className="text-[10px] font-bold text-slate-500 uppercase tracking-widest hover:text-slate-900 transition-colors">Privacy</a>
          </div>
          <p className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.5em]">© 2026 SENTIX CLOUD SYSTEMS.</p>
        </footer>

      </main>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-in { animation: fade-in 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .fade-in { animation-name: fade-in; }
        
        @keyframes slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .slide-in-from-bottom-2 { animation: slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}} />
    </div>
  );
};

export default App;